import React, { type ReactElement } from "react";

/* ---------------- DisabledReason ----------------
   Wraps ONE control and, when `reason` is set, disables it and renders the
   reason as visible muted text right after it, wired by `aria-describedby` —
   the same convention `ActionMenu` and `SegmentedControl` already use for
   their own disabled-with-reason items.

   `reason` is the one documented exception to the C7 naming rule (item and
   option objects use `disabledReason`): the part's own name already says
   what the reason is for.

   With `reason` undefined it returns `children` untouched — no wrapper, no
   clone — so wrapping a control changes nothing until there is a reason to
   state. */

export interface DisabledReasonProps {
  /** Why the control cannot be used. Presence disables it. */
  readonly reason?: string;
  /** The one control this wraps. */
  readonly children: ReactElement;
}

const joinIds = (...ids: (string | undefined)[]) =>
  ids.filter(Boolean).join(" ") || undefined;

export function DisabledReason({
  reason,
  children,
}: DisabledReasonProps): ReactElement {
  const reasonId = React.useId();
  if (reason === undefined) return children;

  const childProps = children.props as Record<string, unknown>;
  const cloned = React.cloneElement(children, {
    disabled: true,
    "aria-describedby": joinIds(
      childProps["aria-describedby"] as string | undefined,
      reasonId,
    ),
  });

  return (
    <>
      {cloned}
      <p id={reasonId} className="mt-1 text-dense-1 text-pb-text-muted">
        {reason}
      </p>
    </>
  );
}
